import { useTranslation } from 'react-i18next'
import { Linkedin, Youtube, Facebook, Instagram, Phone, Mail, MapPin } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { Container } from '@/components/ui/Container'

const socialLinks = [
  { icon: Linkedin, href: 'https://www.linkedin.com/company/gruppo-tecnocart-imballaggi-srl', label: 'LinkedIn' },
  { icon: Youtube, href: 'https://www.youtube.com/@gruppotecnocart', label: 'YouTube' },
  { icon: Facebook, href: 'https://www.facebook.com/gruppotecnocart', label: 'Facebook' },
  { icon: Instagram, href: 'https://www.instagram.com/gruppotecnocart', label: 'Instagram' },
]

export function Footer() {
  const { t } = useTranslation()

  return (
    <footer role="contentinfo">
      {/* Main Footer */}
      <div className="bg-gray-900 py-12 text-white lg:py-16">
        <Container>
          <div className="grid gap-10 lg:grid-cols-12">
            {/* Logo + Tagline */}
            <div className="lg:col-span-4">
              <img
                src="/media/loghi/cropped-logo.bluGT_.news_.png"
                alt="Gruppo Tecnocart"
                className="h-10 w-auto brightness-0 invert"
              />
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-gray-400">
                {t('footer.tagline')}
              </p>
              <div className="mt-6 flex gap-3">
                {socialLinks.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-gray-300 transition-all hover:scale-110 hover:bg-primary-600 hover:text-white"
                  >
                    <s.icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="lg:col-span-2">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-primary-400">
                {t('footer.navigation')}
              </h3>
              <nav className="flex flex-col gap-3" aria-label={t('footer.navigation')}>
                <LocalizedLink to="home" className="text-sm text-gray-300 transition-colors hover:text-white">{t('nav.home')}</LocalizedLink>
                <LocalizedLink to="about" className="text-sm text-gray-300 transition-colors hover:text-white">{t('nav.about')}</LocalizedLink>
                <LocalizedLink to="divisions" className="text-sm text-gray-300 transition-colors hover:text-white">{t('nav.divisions')}</LocalizedLink>
                <LocalizedLink to="certifications" className="text-sm text-gray-300 transition-colors hover:text-white">{t('nav.certifications')}</LocalizedLink>
                <LocalizedLink to="careers" className="text-sm text-gray-300 transition-colors hover:text-white">{t('nav.careers')}</LocalizedLink>
                <LocalizedLink to="contacts" className="text-sm text-gray-300 transition-colors hover:text-white">{t('nav.contacts')}</LocalizedLink>
              </nav>
            </div>

            {/* Services */}
            <div className="lg:col-span-3">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-primary-400">
                {t('nav.divisions')}
              </h3>
              <nav className="flex flex-col gap-3" aria-label={t('nav.divisions')}>
                <LocalizedLink to="divisions" className="text-sm text-gray-300 transition-colors hover:text-white">{t('divisions.scatole.name')}</LocalizedLink>
                <LocalizedLink to="divisions" className="text-sm text-gray-300 transition-colors hover:text-white">{t('divisions.imballaggi.name')}</LocalizedLink>
                <LocalizedLink to="divisions" className="text-sm text-gray-300 transition-colors hover:text-white">{t('divisions.cartotecnica.name')}</LocalizedLink>
                <LocalizedLink to="divisions" className="text-sm text-gray-300 transition-colors hover:text-white">{t('divisions.trasporti.name')}</LocalizedLink>
                <LocalizedLink to="divisions" className="text-sm text-gray-300 transition-colors hover:text-white">{t('divisions.igiene.name')}</LocalizedLink>
              </nav>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-3">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-primary-400">
                {t('footer.contacts')}
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 flex-shrink-0 text-primary-500" />
                  <div className="text-sm text-gray-300">
                    <p className="font-medium text-white">Tecnocart Imballaggi SRL</p>
                    <p className="mt-0.5">Via Torretta 5, 40012<br />Calderara di Reno (BO)</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 flex-shrink-0 text-primary-500" />
                  <a href="tel:051.6815027" className="text-sm text-gray-300 transition-colors hover:text-white">051.6815027</a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 flex-shrink-0 text-primary-500" />
                  <a href="mailto:francesca@tecnocart.net" className="text-sm text-gray-300 transition-colors hover:text-white">francesca@tecnocart.net</a>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Legal */}
      <div className="border-t border-gray-800 bg-gray-900 py-4">
        <Container>
          <div className="flex flex-col items-center justify-between gap-4 text-xs text-gray-500 sm:flex-row">
            <p>&copy; {new Date().getFullYear()} Gruppo Tecnocart. {t('footer.allRightsReserved')}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <LocalizedLink to="privacy" className="transition-colors hover:text-gray-300">{t('footer.privacy')}</LocalizedLink>
              <LocalizedLink to="cookiePolicy" className="transition-colors hover:text-gray-300">{t('footer.cookiePolicy')}</LocalizedLink>
              <LocalizedLink to="environmental" className="transition-colors hover:text-gray-300">{t('nav.environmental')}</LocalizedLink>
              <span className="text-gray-700">|</span>
              <span>P.IVA 02397341203</span>
            </div>
          </div>
        </Container>
      </div>
    </footer>
  )
}
